import { useMemo } from 'react';
import { buildDebriefTrackMap } from '../../utils/debriefTrackMap';
import styles from './DebriefTrackMap.module.css';

function calloutLines(label, maxLength = 18) {
  const words = String(label || '').split(/\s+/).filter(Boolean);
  const lines = [];
  for (const word of words) {
    const current = lines[lines.length - 1];
    if (!current || (current.length + word.length + 1 > maxLength && lines.length < 2)) {
      lines.push(word);
    } else {
      lines[lines.length - 1] = `${current} ${word}`;
    }
  }
  if (lines.length > 2) lines.splice(2);
  if (lines[1]?.length > maxLength + 4) lines[1] = `${lines[1].slice(0, maxLength + 1).trim()}…`;
  return lines;
}

export default function DebriefTrackMap({
  lapTraces,
  selectedLaps,
  corners,
  sectors,
  gaps,
  sectorGaps,
}) {
  const map = useMemo(() => buildDebriefTrackMap({
    lapTraces,
    selectedLaps,
    corners,
    sectors,
    gaps,
    sectorGaps,
  }), [corners, gaps, lapTraces, sectorGaps, sectors, selectedLaps]);

  if (!map) return null;

  const evidenceLabel = map.evidenceKind === 'sector' ? 'Highlighted sectors' : 'Highlighted corners';
  return (
    <figure className={styles.figure} aria-label={`Track coaching map. ${evidenceLabel}.`}>
      <div className={styles.heading}>
        <span>Coaching map</span>
        <small>{evidenceLabel}</small>
      </div>
      <svg
        className={styles.map}
        viewBox={`0 0 ${map.width} ${map.height}`}
        role="img"
        aria-label={map.highlights.map(item => `${item.index}: ${item.label}`).join(', ')}
      >
        <defs>
          <filter id="debrief-map-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <path className={styles.trackShadow} d={map.basePath} />
        <path className={styles.track} d={map.basePath} />
        {map.highlights.map(item => {
          const labelLines = calloutLines(item.label);
          const labelStartY = item.callout.y - ((labelLines.length - 1) * 7);
          return (
            <g key={item.id}>
              <path
                className={styles.highlightGlow}
                d={item.path}
                stroke={item.color}
                filter="url(#debrief-map-glow)"
              />
              <path className={styles.highlight} d={item.path} stroke={item.color} />
              <path
                className={styles.leader}
                d={`M${item.marker.x},${item.marker.y} L${item.callout.x - 12},${item.callout.y}`}
                stroke={item.color}
              />
              <circle className={styles.origin} cx={item.marker.x} cy={item.marker.y} r="5" fill={item.color} />
              <g className={styles.callout}>
                <rect
                  x={item.callout.x - 10}
                  y={item.callout.y - 27}
                  width="140"
                  height="54"
                  rx="8"
                />
                <circle cx={item.callout.x + 10} cy={item.callout.y} r="11" fill={item.color} />
                <text className={styles.calloutNumber} x={item.callout.x + 10} y={item.callout.y + 0.5}>
                  {item.index}
                </text>
                <text className={styles.calloutLabel} x={item.callout.x + 29} y={labelStartY}>
                  {labelLines.map((line, lineIndex) => (
                    <tspan key={line} x={item.callout.x + 29} dy={lineIndex ? 14 : 0}>{line}</tspan>
                  ))}
                </text>
              </g>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
